/**
 * Shared dependencies
 */
import { USER_LISTS_QUERY_KEY } from '../../shared/constants/react-query';
import { waitForStore } from '../../store/utils/waitForStore';
import type {
	CreateUserListParams,
	DeleteUserListParams,
	EditUserListParams,
	AddItemsToUserListItem,
	AddSingleItemToUserListParams,
	RemoveItemsFromUserListParams,
} from '../../store/requests';
import type { List, ListRecordWithItems, ListItem, RecipeListItem } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
const LIST_TYPE_FOLDER = 'RECIPE_FOLDER';

/**
 * Return type for the useFolder hook
 */
export type UseFolderReturn = {
	data: ListRecordWithItems | undefined;
	getFoldersExcluding: ( listsIdsToExclude: string[] ) => ListRecordWithItems;
	isLoading: boolean;
	isFetching: boolean;
	isRefetching: boolean;
	isCreating: boolean;
	isDeleting: boolean;
	isEditing: boolean;
	isAddingItems: boolean;
	isRemovingItems: boolean;
	error: Error | null;
	errorDeleting: Error | null;
	errorCreating: Error | null;
	errorEditing: Error | null;
	errorAddingItems: Error | null;
	errorRemovingItems: Error | null;
	createFolder: (params: CreateUserListParams) => void;
	createFolderAsync: (params: CreateUserListParams) => Promise<List>;
	deleteFolder: (params: DeleteUserListParams) => void;
	deleteFolderAsync: (params: DeleteUserListParams) => Promise<void>;
	editFolder: (params: EditUserListParams) => void;
	editFolderAsync: (params: EditUserListParams) => Promise<List>;
	addRecipeToFolder: (params: { listId: string; item: AddItemsToUserListItem }) => void;
	addRecipeToFolderAsync: (params: AddSingleItemToUserListParams) => Promise<RecipeListItem>;
	removeRecipeFromFolder: (params: RemoveItemsFromUserListParams) => void;
	removeRecipeFromFolderAsync: (params: RemoveItemsFromUserListParams) => Promise<ListItem>;
	refetch: () => Promise<void>;
};

/**
 * Custom hook to manage user folders (lists of type FOLDER)
 *
 * This hook provides functionality to:
 * - Fetch all user lists and filter folders
 * - Create new folders
 * - Manage loading and error states
 *
 * @return Object containing folders data, loading states, and mutation functions
 */
export function useFolder(): UseFolderReturn {
	const { useQuery, useMutation, useQueryClient } = window.valioExternals['react-query']; // Accessing from global externals to minimize bundle size
	const queryClient = useQueryClient();

	if (queryClient === undefined) {
		throw new Error('useFolder must be used within a QueryClientProvider');
	}

	/**
	 * Fetch all user lists
	 * Filter to only include lists of type FOLDER
	 * Manage loading and error states
	 * @return Query object with data, loading states, and error information
	 */
	const { data, ...queryStates } = useQuery<ListRecordWithItems>({
		queryKey: USER_LISTS_QUERY_KEY,
		queryFn: async () => {
			// Check if user is authenticated
			if (!window.valioApp.session.isLoggedIn) {
				return Promise.resolve({});
			}

			// Wait for store to be ready
			const store = await waitForStore();

			// Fetch all lists which updates the store
			await store.useStore().actions.lists.all();

			// Get the current state from the store
			const { data } = store.useStore();

			// Filter Record to only include folders, sort by createDate, and convert back to Record
			const folders = Object.entries<ListRecordWithItems[string]>(data.lists)
				.filter(([, list]) => list.type === LIST_TYPE_FOLDER)
				.sort(
					([, a], [, b]) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
				);

			return Object.fromEntries(folders) as ListRecordWithItems;
		},
	});

	/**
	 * Create a new folder mutation
	 * On success, update the cache and dispatch a ListEvent
	 * @param params Parameters for creating a new user list
	 * @return Mutation object with methods and states
	 */
	const {
		mutate: createFolder,
		mutateAsync: createFolderAsync,
		...createFolderStates
	} = useMutation<List, Error, CreateUserListParams>({
		mutationFn: async (params: CreateUserListParams) => {
			const store = await waitForStore();
			return store.useStore().actions.lists.create(params);
		},
		onSuccess: (newFolder) => {
			// The store is already updated by the action, so we just need to update the query cache
			queryClient.setQueryData<ListRecordWithItems>(USER_LISTS_QUERY_KEY, (oldFolders) => {
				return oldFolders
					? { ...oldFolders, [newFolder.id]: { ...newFolder, items: [] } }
					: { [newFolder.id]: { ...newFolder, items: [] } };
			});

			// Optionally refetch to ensure server sync
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			});
		},
		onError: (error) => {
			// eslint-disable-next-line no-console
			console.error('Failed to create folder:', error);
		},
	});

	/**
	 * Get all folders but exclude certain folders if specified
	 * @param listsToExclude Record of lists to exclude certain folders
	 * @return Filtered record of folders
	 */
	const getFoldersExcluding = (
		listsIdsToExclude: string[]
	): ListRecordWithItems => {
		if (!data) {
			return {};
		}

		const filteredFolders = Object.entries<ListRecordWithItems[string]>(data).filter(
			([id]) => !listsIdsToExclude.includes(id)
		);

		return Object.fromEntries(filteredFolders) as ListRecordWithItems;
	};

	/**
	 * Delete a folder mutation
	 * On success, update the cache and dispatch a ListEvent
	 * @param params Parameters for deleting a user list
	 * @return Mutation object with methods and states
	 */
	const {
		mutate: deleteFolder,
		mutateAsync: deleteFolderAsync,
		...deleteFolderStates
	} = useMutation<void, Error, DeleteUserListParams>({
		mutationFn: async (params: DeleteUserListParams) => {
			const store = await waitForStore();
			return store.useStore().actions.lists.delete(params);
		},
		onSuccess: (_data, params) => {
			// The store is already updated by the action, so we just need to update the query cache
			queryClient.setQueryData<ListRecordWithItems>(USER_LISTS_QUERY_KEY, (oldFolders) => {
				if (!oldFolders) {
					return oldFolders;
				}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { [params.listId]: _, ...remainingFolders } = oldFolders;
				return remainingFolders;
			});

			// Optionally refetch to ensure server sync
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			});
		},
		onError: (error) => {
			// eslint-disable-next-line no-console
			console.error('Failed to delete folder:', error);
		},
	});

	/**
	 * Edit a folder mutation
	 * On success, update the cache and dispatch a ListEvent
	 * @param params Parameters for editing a user list
	 * @return Mutation object with methods and states
	 */
	const {
		mutate: editFolder,
		mutateAsync: editFolderAsync,
		...editFolderStates
	} = useMutation<List, Error, EditUserListParams>({
		mutationFn: async (params: EditUserListParams) => {
			const store = await waitForStore();
			return store.useStore().actions.lists.edit(params);
		},
		onSuccess: (editedFolder) => {
			// The store is already updated by the action, so we just need to update the query cache
			queryClient.setQueryData<ListRecordWithItems>(USER_LISTS_QUERY_KEY, (oldFolders) => {
				if (!oldFolders) {
					return oldFolders;
				}
				return {
					...oldFolders,
					[editedFolder.id]: {
						...editedFolder,
						items: oldFolders[editedFolder.id]?.items || [],
					},
				};
			});

			// Optionally refetch to ensure server sync
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			});
		},
		onError: (error) => {
			// eslint-disable-next-line no-console
			console.error('Failed to edit folder:', error);
		},
	});

	/**
	 * Add recipe to folder mutation
	 * On success, update the cache and dispatch a RecipeEvent
	 * @param params Parameters for adding items to a user list
	 * @return Mutation object with methods and states
	 */
	const {
		mutate: addRecipeToFolder,
		mutateAsync: addRecipeToFolderAsync,
		...addRecipeToFolderStates
	} = useMutation<RecipeListItem, Error, AddSingleItemToUserListParams>({
		mutationFn: async (params: AddSingleItemToUserListParams) => {
			const store = await waitForStore();
			return store.useStore().actions.lists.addSingleItem(params.listId, params.item);
		},
		onSuccess: () => {
			// Optionally refetch to ensure server sync
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			});
		},
		onError: (error) => {
			// eslint-disable-next-line no-console
			console.error('Failed to add recipe to folder:', error);
		},
	});

	/**
	 * Remove recipe from folder mutation
	 * On success, update the cache and dispatch a RecipeEvent
	 * @param params Parameters for removing items from a user list
	 * @return Mutation object with methods and states
	 */
	const {
		mutate: removeRecipeFromFolder,
		mutateAsync: removeRecipeFromFolderAsync,
		...removeRecipeFromFolderStates
	} = useMutation<ListItem, Error, RemoveItemsFromUserListParams>({
		mutationFn: async (params: RemoveItemsFromUserListParams) => {
			const store = await waitForStore();
			return store.useStore().actions.lists.removeItems(params.listId, params.item);
		},
		onSuccess: () => {
			// Optionally refetch to ensure server sync
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			});
		},
		onError: (error) => {
			// eslint-disable-next-line no-console
			console.error('Failed to remove recipe from folder:', error);
		},
	});

	return {
		// Data
		data,
		getFoldersExcluding,

		// Loading states
		isLoading: queryStates.isLoading,
		isFetching: queryStates.isFetching,
		isRefetching: queryStates.isRefetching,
		isCreating: createFolderStates.isPending,
		isDeleting: deleteFolderStates.isPending,
		isEditing: editFolderStates.isPending,
		isAddingItems: addRecipeToFolderStates.isPending,
		isRemovingItems: removeRecipeFromFolderStates.isPending,

		// Error states
		error: queryStates.error,
		errorCreating: createFolderStates.error,
		errorDeleting: deleteFolderStates.error,
		errorEditing: editFolderStates.error,
		errorAddingItems: addRecipeToFolderStates.error,
		errorRemovingItems: removeRecipeFromFolderStates.error,

		// Mutations
		createFolder,
		createFolderAsync,
		deleteFolder,
		deleteFolderAsync,
		editFolder,
		editFolderAsync,
		addRecipeToFolder,
		addRecipeToFolderAsync,
		removeRecipeFromFolder,
		removeRecipeFromFolderAsync,

		// Utility
		refetch: () =>
			queryClient.invalidateQueries({
				queryKey: USER_LISTS_QUERY_KEY,
			}),
	};
}

export type Data = Awaited<ReturnType<typeof useFolder>>['data'];
export type GetFoldersExcluding = Awaited<ReturnType<typeof useFolder>>['getFoldersExcluding'];
export type IsLoading = Awaited<ReturnType<typeof useFolder>>['isLoading'];
export type IsFetching = Awaited<ReturnType<typeof useFolder>>['isFetching'];
export type IsRefetching = Awaited<ReturnType<typeof useFolder>>['isRefetching'];
export type IsCreating = Awaited<ReturnType<typeof useFolder>>['isCreating'];
export type IsDeleting = Awaited<ReturnType<typeof useFolder>>['isDeleting'];
export type IsEditing = Awaited<ReturnType<typeof useFolder>>['isEditing'];
export type IsAddingItems = Awaited<ReturnType<typeof useFolder>>['isAddingItems'];
export type IsRemovingItems = Awaited<ReturnType<typeof useFolder>>['isRemovingItems'];
export type ErrorCreating = Awaited<ReturnType<typeof useFolder>>['errorCreating'];
export type ErrorDeleting = Awaited<ReturnType<typeof useFolder>>['errorDeleting'];
export type ErrorAddingItems = Awaited<ReturnType<typeof useFolder>>['errorAddingItems'];
export type ErrorRemovingItems = Awaited<ReturnType<typeof useFolder>>['errorRemovingItems'];
export type CreateFolder = Awaited<ReturnType<typeof useFolder>>['createFolder'];
export type CreateFolderAsync = Awaited<ReturnType<typeof useFolder>>['createFolderAsync'];
export type DeleteFolder = Awaited<ReturnType<typeof useFolder>>['deleteFolder'];
export type DeleteFolderAsync = Awaited<ReturnType<typeof useFolder>>['deleteFolderAsync'];
export type EditFolder = Awaited<ReturnType<typeof useFolder>>['editFolder'];
export type EditFolderAsync = Awaited<ReturnType<typeof useFolder>>['editFolderAsync'];
export type AddRecipeToFolder = Awaited<ReturnType<typeof useFolder>>['addRecipeToFolder'];
export type AddRecipeToFolderAsync = Awaited<
	ReturnType<typeof useFolder>
>['addRecipeToFolderAsync'];
export type RemoveRecipeFromFolder = Awaited<
	ReturnType<typeof useFolder>
>['removeRecipeFromFolder'];
export type RemoveRecipeFromFolderAsync = Awaited<
	ReturnType<typeof useFolder>
>['removeRecipeFromFolderAsync'];
